import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Autocomplete, createTheme, TextField} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {makeStyles} from '@mui/styles';
import {useDebounce} from '../../_helpers';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {deleteEmailsFromList, mergeEmailsList, validateEmail} from '../../../utils/email';
import {TooltipWrapperIssue} from '../../TooltipWrapperIssue.jsx';
import {LAMBDA_NAMES, NO_OPTIONS_ARRAY, NO_OPTIONS_LABEL} from '../../../utils/constants';
import {getUserEmailsGraphAPILambda, toErrorFlow} from '../../../utils/functionSignatures';
import {BrickContext} from 'olympe';

/**
 * @param {UserFieldMultiSelect.propTypes} props
 * @return {JSX.Element}
 */
export function UserFieldMultiSelect(props) {
    const useRequestStore = requestStores.get(props.prTag);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);

    const [otherAddress, setOtherAddress] = useRequestStore((state) => [state.otherAddress, state.setOtherAddress]);
    const p2pOther = useRequestStore((state) => state.p2pOther);
    const p2pOtherSupplierAddresses = useRequestStore((state) => state.p2pOtherSupplierAddresses);

    const [textInput, setTextInput] = useState(otherAddress);
    const [queriedText, setQueriedText] = useState('');
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const debouncedTextInput = useDebounce(textInput, 800);
    const [showErrorTooltip, setShowErrorTooltip] = useState(false);
    const [error, setError] = useState({code: 0, body: ''});
    const [selectedEmails, setSelectedEmails] = useState(otherAddress ? otherAddress?.split(';')?.map((address) => ({
        label: '',
        value: '',
        email: address?.toLowerCase(),
    })) : []);

    // Merge p2pSupplierAddresses with otherAddress
    useEffect(() => {
        let mergedOtherAddresses;
        const otherAddressToArray = otherAddress?.split(';') || [];
        if (p2pOther) {
            mergedOtherAddresses = mergeEmailsList(otherAddressToArray, p2pOtherSupplierAddresses);
        } else {
            mergedOtherAddresses = deleteEmailsFromList(otherAddressToArray, p2pOtherSupplierAddresses);
        }
        const values = mergedOtherAddresses.map((address) => ({
            label: '',
            value: '',
            email: address?.toLowerCase(),
        }));
        const emails = values?.map((mail) => mail.email)?.join(';');
        setOtherAddress(emails);
        setSelectedEmails(values);
    }, [p2pOther, p2pOtherSupplierAddresses]);

    const checkEmailAddresses = (value) => {
        if ( !value ) {
            return false;
        }
        if (value === '') { // An empty string is a valid value
            return true;
        }
        const emails = value.split(';').filter((e) => e !== '');

        return emails.every((email) => validateEmail(email.trim()));
    };

    const [otherFieldError, setOtherFieldError] = useState(!checkEmailAddresses(otherAddress));

    const getUsersByEmailsGraphAPI = props.context.get(LAMBDA_NAMES.GET_USERS_BY_EMAIL_GRAPH_API);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    // This useEffect handles the queries to perform to get the options for the autocomplete
    // it will initiate queries when textInput changes (with debouncing)
    useEffect(() => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }
        const searchInput = debouncedTextInput?.[0] || '';
        // avoid performing a query of with the same input as the last query
        if (queriedText === searchInput && options.length > 0) {
            return;
        }
        // avoid performing a query when the autocomplete is closed
        if (open === false) {
            return;
        }
        // avoid performing a query when the input text is < 3 characters
        if (searchInput.length < 3) {
            setOptions([{label: 'Min. 3 characters', value: ''}]);
            return;
        }
        // start a query
        setIsPerformingQuery(true);
        setShowErrorTooltip(false);

        (async () => {
            getUserEmailsGraphAPILambda(props.context,
                getUsersByEmailsGraphAPI,
                searchInput)
                .then((resultOut) => {
                    const result = JSON.parse(resultOut[0]).value
                        .filter((userInfo) => !!userInfo['onPremisesSamAccountName'] && !!userInfo['mail'])
                        .map((userInfo) => {
                            return {
                                label: '',
                                value: '',
                                email: userInfo['mail']?.toLowerCase(),
                            };
                        });

                    const resultWithoutDuplicates = result.filter((value, index, self) => // Remove duplicates
                        index === self.findIndex((o) => o.email === value.email));

                    // Sort alphabetically
                    const sortedResults = resultWithoutDuplicates.sort((a, b) => a.email.localeCompare(b.email));

                    setOptions(sortedResults.length > 0 ? sortedResults : NO_OPTIONS_ARRAY);
                    setQueriedText(searchInput);
                    setIsPerformingQuery(false);
                })
                .catch((reason) => {
                    const errorFlow = toErrorFlow(reason);
                    setShowErrorTooltip(true);
                    setError({code: errorFlow.getCode(), body: errorFlow.getMessage()});
                    setIsPerformingQuery(false);
                });
        })();
    }, [debouncedTextInput, open, queriedText]);

    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        setTextInput(typeof inputValue === 'string' ? inputValue.split(',') : inputValue);
    };

    const handleChange = (event, values) => {
        const emails = values?.map((mail) => mail.email)?.join(';');
        if (emails && checkEmailAddresses(emails)) {
            setOtherAddress(emails);
            setSelectedEmails(values);
            setOtherFieldError(false);
        } else if (emails === '') {
            setOtherAddress(emails);
            setSelectedEmails([]);
            setOtherFieldError(false);
        } else if (emails) {
            setOtherFieldError(true);
        }
    };

    const _theme = createTheme();
    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                },
            };
        },
        {_theme},
    );
    const classes = useStyles();

    return (
        <div className={classes.root} style={{marginTop: '20px', width: '100%'}}>
            <TooltipWrapperIssue
                open={showErrorTooltip}
                onClose={() => setShowErrorTooltip(false)}
                error={error}
            >
                <Autocomplete
                    multiple
                    forcePopupIcon
                    fullWidth
                    size={'small'}
                    open={open}
                    options={options}
                    getOptionDisabled={(option) => option.label === NO_OPTIONS_LABEL || option.label === 'Min. 3 characters'}
                    getOptionLabel={(option) => option.label === NO_OPTIONS_LABEL || option.label === 'Min. 3 characters' ? option.label : option?.email?.toLowerCase()}
                    value={selectedEmails}
                    filterSelectedOptions
                    isOptionEqualToValue={(option, value) => option.email === value.email}
                    onChange={handleChange}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    loading={isPerformingQuery}
                    disabled={props.disabled}
                    renderOption={(liProps, option) => (
                        <li {...liProps} data-cy={'nestle-email-option'}>
                            {option.label === NO_OPTIONS_LABEL || option.label === 'Min. 3 characters' ?
                                option.label :
                                option?.email?.toLowerCase()}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            data-cy={'nestle-email-field'}
                            variant={'standard'}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <React.Fragment>
                                        {isPerformingQuery || textInput !== debouncedTextInput ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.startAdornment}
                                    </React.Fragment>
                                ),
                            }}
                            onChange={handleInputChange}
                            error={otherFieldError}
                        />
                    )}
                />
            </TooltipWrapperIssue>
        </div>
    );
}

UserFieldMultiSelect.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    prTag: PropTypes.string,
    disabled: PropTypes.bool,
};
